/**
 * Serializers for converting data between formats
 * Supports JSON, YAML, XML, TOML
 */

import yaml from 'js-yaml'
import type { DataFormat } from '@/types'

// XML Builder helper
function buildXML(data: unknown, rootName: string = 'root', indent: number = 0): string {
  const spaces = '  '.repeat(indent)

  if (data === null || data === undefined) {
    return `${spaces}<${rootName}/>`
  }

  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    const escaped = String(data)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
    return `${spaces}<${rootName}>${escaped}</${rootName}>`
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return `${spaces}<${rootName}/>`
    }
    const items = data.map((item) => buildXML(item, 'item', indent + 1)).join('\n')
    return `${spaces}<${rootName}>\n${items}\n${spaces}</${rootName}>`
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>)
    if (entries.length === 0) {
      return `${spaces}<${rootName}/>`
    }
    const children = entries
      .map(([key, value]) => {
        // Sanitize key for XML tag name
        const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^[^a-zA-Z_]/, '_$&')
        return buildXML(value, safeKey, indent + 1)
      })
      .join('\n')
    return `${spaces}<${rootName}>\n${children}\n${spaces}</${rootName}>`
  }

  return `${spaces}<${rootName}>${String(data)}</${rootName}>`
}

// TOML Builder helper
function buildTOML(data: unknown, prefix: string = ''): string {
  if (data === null || data === undefined) {
    return ''
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('TOML root must be an object')
  }

  const lines: string[] = []
  const tables: string[] = []

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const safeKey = /^[a-zA-Z0-9_-]+$/.test(key) ? key : `"${key.replace(/"/g, '\\"')}"`

    if (value === null || value === undefined) {
      // Skip null values in TOML
      continue
    }

    if (typeof value === 'string') {
      // Multi-line strings
      if (value.includes('\n')) {
        lines.push(`${safeKey} = """`)
        lines.push(value)
        lines.push('"""')
      } else {
        lines.push(`${safeKey} = "${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
      }
    } else if (typeof value === 'number') {
      lines.push(`${safeKey} = ${value}`)
    } else if (typeof value === 'boolean') {
      lines.push(`${safeKey} = ${value}`)
    } else if (Array.isArray(value)) {
      // Check if array of primitives or objects
      const hasPrimitives = value.some(v => typeof v !== 'object' || v === null)
      const hasObjects = value.some(v => typeof v === 'object' && v !== null && !Array.isArray(v))

      if (hasObjects && !hasPrimitives) {
        // Array of tables
        for (const item of value) {
          const tableName = prefix ? `${prefix}.${safeKey}` : safeKey
          tables.push(`\n[[${tableName}]]`)
          tables.push(buildTOML(item, ''))
        }
      } else {
        // Inline array
        const items = value.map(v => {
          if (typeof v === 'string') return `"${v.replace(/"/g, '\\"')}"`
          if (typeof v === 'object' && v !== null) return JSON.stringify(v)
          return String(v)
        })
        lines.push(`${safeKey} = [${items.join(', ')}]`)
      }
    } else if (typeof value === 'object') {
      // Nested table
      const tableName = prefix ? `${prefix}.${safeKey}` : safeKey
      tables.push(`\n[${tableName}]`)
      tables.push(buildTOML(value, tableName))
    }
  }

  return lines.join('\n') + tables.join('\n')
}

/**
 * Serialize data to JSON format
 */
export function serializeToJSON(data: unknown, pretty: boolean = true): string {
  return pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data)
}

/**
 * Serialize data to YAML format
 */
export function serializeToYAML(data: unknown): string {
  return yaml.dump(data, {
    indent: 2,
    lineWidth: -1,
    noRefs: true,
    sortKeys: false,
  })
}

/**
 * Serialize data to XML format
 */
export function serializeToXML(data: unknown, rootName: string = 'root'): string {
  const xmlContent = buildXML(data, rootName)
  return `<?xml version="1.0" encoding="UTF-8"?>\n${xmlContent}`
}

/**
 * Serialize data to TOML format
 */
export function serializeToTOML(data: unknown): string {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new Error('TOML root must be an object')
  }
  return buildTOML(data).trim()
}

/**
 * Convert content from one format to another
 */
export function convertFormat(
  data: unknown,
  targetFormat: DataFormat
): string {
  switch (targetFormat) {
    case 'json':
      return serializeToJSON(data)
    case 'yaml':
      return serializeToYAML(data)
    case 'xml':
      return serializeToXML(data)
    case 'toml':
      return serializeToTOML(data)
    default:
      return serializeToJSON(data)
  }
}

/**
 * Get file extension for a format
 */
export function getFormatExtension(format: DataFormat): string {
  switch (format) {
    case 'json': return 'json'
    case 'yaml': return 'yaml'
    case 'xml': return 'xml'
    case 'toml': return 'toml'
    case 'edi': return 'edi'
    case 'ofx': return 'ofx'
    case 'cnab': return 'cnab'
    default: return 'txt'
  }
}

/**
 * Get MIME type for a format
 */
export function getFormatMimeType(format: DataFormat): string {
  switch (format) {
    case 'json': return 'application/json'
    case 'yaml': return 'application/x-yaml'
    case 'xml': return 'application/xml'
    case 'toml': return 'application/toml'
    default: return 'text/plain'
  }
}
